import React, { useState, useRef } from 'react';
import { Upload, Download, Edit3, Trash2, Loader, X, AlertCircle, CheckCircle } from 'lucide-react';
import { uploadFileToS3, validateFile } from '../../utils/s3Upload';
import { useToast } from '../../hooks/useToast';
import { ConfirmDialog } from '../shared/ConfirmDialog';

const FileUploadButton = ({
  file,
  onUpload,
  onDownload,
  onDelete,
  presignFn,
  completeFn,
  downloadFn,
  deleteFn,
  allowedTypes = ['.pdf', '.doc', '.docx'],
  uploadText = 'Upload',
  className = '',
  disabled = false,
  variant = "upload"
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const { showToast } = useToast();
  const abortControllerRef = useRef(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleFileSelect = async (selectedFile) => {
    if (!selectedFile || uploading) return;

    try {
      validateFile(selectedFile, allowedTypes);
      setUploading(true);
      setProgress(0);
      
      showToast('Upload started...', 'info');

      const result = await uploadFileToS3(
        selectedFile,
        presignFn,
        completeFn,
        setProgress
      );

      showToast('Upload completed – analysis pending...', 'success');
      
      if (onUpload) {
        onUpload(result);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      showToast(`Upload failed: ${error.message}`, 'error');
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownload = async () => {
    try {
      const downloadData = await downloadFn();
      window.open(downloadData.downloadUrl, '_blank');
      
      if (onDownload) {
        onDownload();
      }
    } catch (error) {
      console.error('Download failed:', error);
      showToast(`Download failed: ${error.message}`, 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteFn();
      showToast('File deleted successfully', 'success');

      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      console.error('Delete failed:', error);
      showToast(`Delete failed: ${error.message}`, 'error');
    }
  };

  const triggerFileSelect = () => {
    if (fileInputRef.current && !uploading && !disabled) {
      fileInputRef.current.click();
    }
  };

  const uploadFile = async (file) => {
    try {
      setUploading(true);
      setProgress(0);
      setShowModal(true);
      setError(null);

      // Create abort controller for cancellation
      abortControllerRef.current = new AbortController();

      // Step 1: Get presigned URL
      const presignData = await presignFn(file);
      const { uploadUrl, objectKey } = presignData;

      // Step 2: Upload to S3
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setProgress(progress);
        }
      });

      xhr.addEventListener('load', async () => {
        if (xhr.status === 200) {
          try {
            // Step 3: Complete upload
            const result = await completeFn({
              objectKey,
              filename: file.name
            });
            
            setUploading(false);
            setShowModal(false);
            onUpload?.(result);
          } catch (completeError) {
            setError('Failed to complete upload: ' + completeError.message);
            setUploading(false);
          }
        } else {
          setError('Upload failed with status: ' + xhr.status);
          setUploading(false);
        }
      });

      xhr.addEventListener('error', () => {
        setError('Upload failed due to network error');
        setUploading(false);
      });

      xhr.addEventListener('abort', () => {
        setError('Upload cancelled');
        setUploading(false);
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);

      // Store xhr for potential cancellation
      abortControllerRef.current.xhr = xhr;

    } catch (error) {
      setError('Failed to start upload: ' + error.message);
      setUploading(false);
      onUpload?.(null);
    }
  };

  const cancelUpload = () => {
    if (abortControllerRef.current?.xhr) {
      abortControllerRef.current.xhr.abort();
    }
    setUploading(false);
    setShowModal(false);
    setProgress(0);
  };

  const retryUpload = () => {
    if (fileInputRef.current?.files?.[0]) {
      uploadFile(fileInputRef.current.files[0]);
    }
  };

  const closeModal = () => {
    if (!uploading) {
      setShowModal(false);
      setError(null);
      setProgress(0);
    }
  };

  if (uploading) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className="flex items-center px-3 py-2 text-xs bg-blue-100 text-blue-800 rounded-md">
          <Loader className="animate-spin mr-2" size={12} />
          Uploading... {progress}%
        </div>
      </div>
    );
  }

  if (file) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <button
          onClick={handleDownload}
          className="flex items-center px-3 py-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors border border-blue-200"
          disabled={disabled}
          title="Download file"
        >
          <Download size={12} className="mr-1" />
          Download
        </button>
        
        <button
          onClick={triggerFileSelect}
          className="flex items-center px-3 py-2 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors border border-gray-200"
          disabled={disabled}
          title="Replace file"
        >
          <Edit3 size={12} className="mr-1" />
          Replace
        </button>
        
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="flex items-center px-3 py-2 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors border border-red-200"
          disabled={disabled}
          title="Delete file"
        >
          <Trash2 size={12} className="mr-1" />
          Delete
        </button>

        <ConfirmDialog
          open={showDeleteConfirm}
          title="Delete File"
          message="Are you sure you want to delete this file? This action cannot be undone."
          confirmLabel="Delete"
          variant="danger"
          onConfirm={() => { setShowDeleteConfirm(false); handleDelete(); }}
          onCancel={() => setShowDeleteConfirm(false)}
        />

        <input
          ref={fileInputRef}
          type="file"
          onChange={(e) => handleFileSelect(e.target.files[0])}
          className="hidden"
          accept={allowedTypes.join(',')}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <button
        onClick={triggerFileSelect}
        className="flex items-center px-3 py-2 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm"
        disabled={disabled || uploading}
      >
        <Upload size={12} className="mr-1" />
        {uploadText}
      </button>

      <input
        ref={fileInputRef}
        type="file"
        onChange={(e) => handleFileSelect(e.target.files[0])}
        className="hidden"
        accept={allowedTypes.join(',')}
      />

      {/* Upload Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {uploading ? 'Uploading File...' : error ? 'Upload Failed' : 'Upload Complete'}
              </h3>
              {!uploading && (
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {uploading && (
              <div className="space-y-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-600 text-center">
                  {progress}% complete
                </p>
                <div className="flex justify-center">
                  <button
                    onClick={cancelUpload}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="w-5 h-5" />
                  <span className="text-sm">{error}</span>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Close
                  </button>
                  <button
                    onClick={retryUpload}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Retry
                  </button>
                </div>
              </div>
            )}

            {!uploading && !error && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm">File uploaded successfully!</span>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadButton; 